import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Modal,
  NumberStepper,
  Radio,
  Select,
  Switch,
  Text,
} from '@kula-ai/components';
import { Box, Flex, Stack } from '@chakra-ui/react';
import { useShowToast } from '@/hooks/useShowToast';
import { CONSENT_ACTIONS_API } from './api';
import { consentErrorMessage } from './consentStatus';
import type { ConsentTarget } from './types';

const UNIT_OPTIONS = [
  { label: 'Days', value: 'days' },
  { label: 'Months', value: 'months' },
  { label: 'Years', value: 'years' },
];

type ExtendUnit = 'days' | 'months' | 'years';
type Outcome = 'granted' | 'withdrawn';

interface MarkConsentModalProps {
  open: boolean;
  /** Current consent is directly extendable (granted / no_expiry) — skip
   *  mark_granted and just extend. Otherwise grant first, then extend. */
  isExtendable: boolean;
  /** Current consent can be withdrawn (BE allows requested / granted / no_expiry). */
  canWithdraw: boolean;
  target: ConsentTarget;
  onClose: () => void;
  invalidateKeys?: unknown[][];
  /** Fires after a successful save (e.g. to refetch a Redux-backed surface). */
  onSuccess?: () => void;
}

// Product folded mark granted / not required / withdraw / extend into one
// validity editor: consented (with a validity or no expiry) or withdrawn.
export const MarkConsentModal = ({
  open,
  isExtendable,
  canWithdraw,
  target,
  onClose,
  invalidateKeys,
  onSuccess,
}: MarkConsentModalProps) => {
  const showToast = useShowToast();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<Outcome>('granted');
  const [value, setValue] = useState(6);
  const [unit, setUnit] = useState<ExtendUnit>('months');
  const [noExpiry, setNoExpiry] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      if (outcome === 'withdrawn') {
        return CONSENT_ACTIONS_API.postLifecycleAction('withdraw', target);
      }
      // mark_granted first when not directly extendable; extend sets validity
      if (!isExtendable) {
        await CONSENT_ACTIONS_API.postLifecycleAction('mark_granted', target);
      }
      return CONSENT_ACTIONS_API.postExtend(
        target,
        noExpiry ? { no_expiry: true } : { value, unit }
      );
    },
    onSuccess: () => {
      invalidateKeys?.forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey })
      );
      showToast({ title: 'Consent updated', status: 'success' });
      onSuccess?.();
      onClose();
    },
    onError: (err) =>
      showToast({ title: consentErrorMessage(err), status: 'error' }),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(v: boolean) => !v && onClose()}
      title="Mark consent manually"
      description="Record consent collected outside Kula, change its validity, or withdraw it."
      footer={
        <Flex justify="flex-end" gap="8px" w="100%">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
          >
            Save
          </Button>
        </Flex>
      }
    >
      <Stack spacing="16px">
        <Flex align="flex-start" gap="10px">
          <Radio
            checked={outcome === 'granted'}
            onChange={() => setOutcome('granted')}
            size="sm"
            label="Consented"
          />
        </Flex>
        <Box
          pl="24px"
          opacity={outcome === 'granted' ? 1 : 0.4}
          pointerEvents={outcome === 'granted' ? 'auto' : 'none'}
        >
          <Stack spacing="12px">
            <Box
              opacity={noExpiry ? 0.4 : 1}
              pointerEvents={noExpiry ? 'none' : 'auto'}
            >
              <Flex align="center" gap="8px">
                <Text fontSize="13px" color="secondary.700" flexShrink={0}>
                  Valid for
                </Text>
                <Box w="140px">
                  <NumberStepper min={1} value={value} onChange={setValue} />
                </Box>
                <Box w="130px">
                  <Select
                    options={UNIT_OPTIONS}
                    value={unit}
                    menuPortalTarget={null}
                    reactSelectProps={{ menuPosition: 'fixed' }}
                    onChange={(v) => setUnit(v as ExtendUnit)}
                  />
                </Box>
              </Flex>
            </Box>
            <Switch
              checked={noExpiry}
              onCheckedChange={setNoExpiry}
              label="Consent never expires"
              size="sm"
            />
          </Stack>
        </Box>
        {/* Withdraw only when the BE allows it (requested / granted /
            no_expiry) — an already withdrawn/refused/expired consent can't. */}
        {canWithdraw && (
          <>
            <Radio
              checked={outcome === 'withdrawn'}
              onChange={() => setOutcome('withdrawn')}
              size="sm"
              label="Withdrawn"
            />
            {outcome === 'withdrawn' && (
              <Text fontSize="13px" color="secondary.700">
                The candidate will no longer be processable until they consent
                again.
              </Text>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
};
